import { connect } from 'formik';
import React, { Component, PropsWithChildren } from 'react';
import RWDropdown from 'react-widgets/lib/DropdownList';
import { RegisterElement } from '..';
import { getDescribedBy } from '../Input/inputUtils';
import { IDropdownProps } from '../Input/types';
import { FieldHelpersWrapper, wrapperClasses } from '../utils/formHelpers';
import { resolveProps } from '../utils/formikUtils';
import { createId } from '../utils/IdGenerator';
import { sizeStyles } from '../utils/sizes';
import './Dropdown.scss';
import { IDropdownOption, InnerDropdownProps } from './types';

interface IDropdownState {
  isOpen: boolean;
}

/**
 * Controlled Input: Dropdown
 */
export class Dropdown extends Component<PropsWithChildren<InnerDropdownProps>, IDropdownState> {
  public state = {
    isOpen: false,
  };
  private id = this.props.id || createId('dropdown');

  public componentDidMount() {
    this.props.formik.registerField && this.props.formik.registerField(this.props.name, this);
    window.document.addEventListener('keydown', this.onEscape, true);
  }

  public componentWillUnmount() {
    this.props.formik.unregisterField && this.props.formik.unregisterField(this.props.name);
    window.document.removeEventListener('keydown', this.onEscape, true);
  }

  public render() {
    const { id } = this;
    const {
      options,
      filter,
      onSearch,
      groupBy,
      groupComponent,
      emptyValue,
      formik,
      busy,
      name,
      helpText,
      displaySuccess = false,
      validate,
      onChange,
      placeholder,
      required,
      sizePreset,
      messages,
      ...inputAttrsProps
    } = this.props;
    const { valid = true, touched = false, onBlur, value } = resolveProps(this.props, this.props.formik);
    const showError = touched && !valid;
    const showSuccess = displaySuccess && touched && valid;
    const describedBy = getDescribedBy({
      hasErrors: showError,
      hasHelpText: !!helpText,
      id,
    });

    const itemComponent = ({
      item,
    }: {
      item: {
        value: string | number;
        display?: string;
        subtext?: string;
      };
    }) => {
      return (
        <React.Fragment>
          {item.display} {item.subtext ? <span className="subtext">{item.subtext}</span> : null}
        </React.Fragment>
      );
    };

    let optionList: IDropdownOption[] = options.map((option) => ({
      display: option.display || option.value.toString(),
      subtext: option.subtext,
      value: option.value,
    }));

    // adding an "emptyValue" will not work when option values are objects
    if (emptyValue && optionList[0] && typeof optionList[0].value !== 'object') {
      const op: IDropdownOption[] = [
        {
          display: emptyValue,
          subtext: '',
          value: '',
        },
      ];
      optionList = op.concat(optionList);
    }

    return (
      <RegisterElement name={`register-${id}`}>
        {(ref: any) => {
          const myRef = showError ? ref : undefined;
          return (
            <div
              ref={myRef}
              id={`${id}-wrapper`}
              className={'hlc-dropdown'}
              qa-id={this.props['qa-id']}
              style={sizeStyles(sizePreset)}
            >
              <FieldHelpersWrapper id={id} props={this.props}>
                <div aria-describedby={describedBy} className={wrapperClasses({ showError, showSuccess })}>
                  <RWDropdown
                    aria-labelledby={`${id}-label`}
                    data={optionList}
                    filter={filter}
                    textField="display"
                    valueField="value"
                    busy={busy}
                    defaultValue={emptyValue || value}
                    messages={messages}
                    onChange={this.handleChange}
                    onSearch={onSearch}
                    onToggle={this.onToggle}
                    open={this.state.isOpen}
                    groupBy={groupBy}
                    groupComponent={groupComponent}
                    itemComponent={itemComponent}
                    inputProps={{
                      id,
                      name,
                      onBlur: (e: any) => {
                        onBlur(e);
                      },
                    }}
                    {...inputAttrsProps}
                    value={value}
                    placeholder={placeholder}
                  />
                </div>
              </FieldHelpersWrapper>
            </div>
          );
        }}
      </RegisterElement>
    );
  }

  private onEscape = (e: KeyboardEvent) => {
    if (this.state.isOpen && e.keyCode === 27) {
      this.setState({ isOpen: false });
      e.stopPropagation();
    }
  };
  private handleChange = (selected: { value: string | number; display?: string }) => {
    if (this.props.onChange) {
      this.props.onChange(this.props.name, selected.value);
    } else if (this.props.formik && this.props.formik.setFieldValue) {
      this.props.formik.setFieldValue(this.props.name, selected.value);
    }
  };

  private onToggle = (data: boolean) => {
    this.setState({ isOpen: data });
    if (!data) {
      this.props.formik &&
        this.props.formik.setFieldTouched &&
        this.props.formik.setFieldTouched(this.props.name, true);
    }
  };
}

export default connect<IDropdownProps>(Dropdown);
